import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import { isSessionExpired } from '@/_helpers/requests'

Vue.use(VueRouter)

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [{
    path: '/',
    name: 'home',
    component: () => import('@/pages/Home.vue'),
    meta: {
      requires: ['location'],
    },
  },
  {
    path: '/account',
    name: 'account',
    component: () => import('@/pages/Account.vue'),
    meta: {
      requires: ['location'],
    },
  },
  {
    path: '/organization',
    name: 'organization',
    component: () => import('@/pages/Organization.vue'),
  },
  {
    path: '/preferences',
    name: 'preferences',
    component: () => import('@/pages/Preferences.vue'),
    meta: {
      requires: ['location'],
    },
  },
  {
    path: '/blocked-days',
    name: 'blocked-days',
    component: () => import('@/pages/BlockedDays.vue'),
    meta: {
      requires: ['location'],
    },
  },
  {
    path: '/reviews',
    name: 'reviews',
    component: () => import('@/pages/Reviews.vue'),
    meta: {
      requires: ['location'],
    },
  },
  {
    path: '/forgot-password',
    name: 'auth-forgot-password-v2',
    component: () => import('@/pages/ForgotPassword.vue'),
    meta: {
      layout: 'full',
    },
  },
  {
    path: '/setup-password',
    name: 'setup-password',
    component: () => import('@/pages/SetupPassword.vue'),
    meta: {
      layout: 'full',
    },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/pages/ResetPassword.vue'),
    meta: {
      layout: 'full',
    },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/Login.vue'),
    meta: {
      layout: 'full',
    },
  },
  {
    path: '/session-unavailable',
    name: 'session-unavailable',
    component: () => import('@/pages/SessionUnavailable.vue'),
    meta: {
      layout: 'full',
    },
  },
  {
    path: '/error-404',
    name: 'error-404',
    component: () => import('@/pages/error/Error404.vue'),
    meta: {
      layout: 'full',
    },
  },
  {
    path: '*',
    redirect: 'login',
  },
  ],
})

const publicPages = ['/login', '/setup-password', '/forgot-password', '/reset-password']

// Pages reachable while authenticated without the essential entities loaded.
const entityExemptRoutes = ['session-unavailable', 'error-404']

router.beforeEach(async (to, from, next) => {
  if (publicPages.includes(to.path)) {
    return next()
  }

  const loggedIn = localStorage.getItem('auth')
  if (!loggedIn) {
    return next('/login')
  }

  if (entityExemptRoutes.includes(to.name)) {
    return next()
  }

  // A previously-detected expired session: send straight to the re-login screen.
  if (isSessionExpired()) {
    return next({ name: 'session-unavailable', query: { reason: 'expired' } })
  }

  // Load the essential entities (partner -> organization -> locations) once,
  // and route to a friendly error if anything required is missing.
  const status = await store.dispatch('authentication/ensureHydrated')
  // Hydration itself can surface an expired session (401 on partner/me).
  if (isSessionExpired()) {
    return next({ name: 'session-unavailable', query: { reason: 'expired' } })
  }
  if (status === 'error') {
    return next({ name: 'session-unavailable', query: { reason: 'hydration' } })
  }

  if (!store.state.authentication.partner) {
    return next({ name: 'session-unavailable', query: { reason: 'partner' } })
  }
  if (!store.getters['authentication/organization']) {
    return next({ name: 'session-unavailable', query: { reason: 'organization' } })
  }

  const requires = (to.meta && to.meta.requires) || []
  if (requires.includes('location') && !store.getters['authentication/location']) {
    return next({ name: 'session-unavailable', query: { reason: 'location' } })
  }

  return next()
})

export default router
